import { Tabbar } from '@telegram-apps/telegram-ui'
import type { TabbarItemProps } from '@telegram-apps/telegram-ui/dist/components/Layout/Tabbar/components/TabbarItem/TabbarItem'
import type { ReactElement } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useAuthStore } from 'shared/store/auth'
import { hasAdminPermission } from 'shared/lib/permissions/permissions'
import { useTranslate } from 'shared/lib/i18n'
import { ErrorFallbackMain } from '../error-boundaries/ErrorFallback'
import { GET_ROUTES_WITH_PERMISSIONS } from '../routes'
import { TelegramBackButtonController } from 'shared/components/TelegramBackButtonController'
import { AppInitializers } from './AppInitializers'

export function AppLayout() {
	const navigate = useNavigate()
	const location = useLocation()
	const user = useAuthStore((s) => s.user)
	const { t } = useTranslate()

	const isAdmin = hasAdminPermission(user)

	const titled = GET_ROUTES_WITH_PERMISSIONS({
		user,
		isRenderingOnNavbar: true,
	})
	const tabs = isAdmin ? titled.filter((t) => t.group === 'admin') : titled

	const selectedPath =
		location.pathname === '/orders'
			? '/profile'
			: tabs
					.filter(
						(t) =>
							location.pathname === t.path ||
							(t.path !== '/' && location.pathname.startsWith(t.path + '/')),
					)
					.sort((a, b) => b.path.length - a.path.length)[0]?.path

	/**
	 * A tab tap always brings the page back to the top. Tapping the tab you are
	 * already on is the usual escape from a long feed, so that one animates;
	 * a tab that navigates jumps, because smooth-scrolling content that is
	 * about to be replaced only looks like a glitch. The page itself scrolls
	 * (the window), not a nested container.
	 */
	function openTab(path: string) {
		const isCurrentPage = location.pathname === path
		if (!isCurrentPage) navigate(path)
		window.scrollTo({ top: 0, behavior: isCurrentPage ? 'smooth' : 'auto' })
	}

	const tabItems: ReactElement<TabbarItemProps>[] = tabs.map((tab) => (
		<Tabbar.Item
			key={tab.path}
			text={t(tab.title!)}
			selected={tab.path === selectedPath}
			onClick={() => openTab(tab.path)}
		>
			{tab.icon}
		</Tabbar.Item>
	))

	return (
		<div className='flex min-h-svh flex-col'>
			<TelegramBackButtonController />
			<main className='flex-1 overflow-y-auto pt-(--header-safe-top) pb-[calc(60px+var(--tabbar-safe-bottom))]'>
				<ErrorBoundary FallbackComponent={ErrorFallbackMain}>
					<Outlet />
				</ErrorBoundary>
			</main>

			<Tabbar className='app-tabbar z-1'>{tabItems}</Tabbar>

			<AppInitializers />
		</div>
	)
}

/*
// toggleView and adminView logic moved from AppLayout:
// const adminView = useUiStore((s) => s.adminView)
// const setAdminView = useUiStore((s) => s.setAdminView)

// useEffect(() => {
// 	if (isAdmin && adminView === 'admin' && location.pathname === '/') {
// 		navigate('/admin', { replace: true })
// 	}
// }, [isAdmin, adminView, location.pathname, navigate])

// function toggleView() {
// 	const next = adminView === 'admin' ? 'shop' : 'admin'
// 	setAdminView(next)
// 	navigate(next === 'admin' ? '/admin' : '/')
// }

// if (isAdmin) {
// 	tabItems.push(
// 		<Tabbar.Item
// 			key='__toggle'
// 			text={adminView === 'admin' ? 'Витрина' : 'Админка'}
// 			selected={false}
// 			onClick={toggleView}
// 		>
// 			<RefreshCw />
// 		</Tabbar.Item>,
// 	)
// }
*/
